import { Link } from 'react-router-dom';
import { BackButton } from '../../components/ui/BackButton';
import { ROUTES, SHOPPER_HOME } from '../../constants/routes';

const EFFECTIVE_DATE = 'August 7, 2026';
const LAST_UPDATED = 'August 7, 2026';

interface SectionProps {
  id: string;
  number: string;
  title: string;
  children: React.ReactNode;
}

function Section({ id, number, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-serif text-heading mb-3">
        {number}. {title}
      </h2>
      <div className="space-y-3 text-sm text-body leading-relaxed">{children}</div>
    </section>
  );
}

export function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center">
            <BackButton fallback={SHOPPER_HOME} />
            <h1 className="ml-4 text-xl font-serif text-heading">Privacy Policy</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 pb-24">
        <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8 space-y-8">
          <div className="border-b border-gray-100 pb-6">
            <p className="text-4xl font-serif text-primary mb-2">Fash</p>
            <h2 className="text-2xl font-serif text-heading">Privacy Policy</h2>
            <p className="text-sm text-body mt-3">
              Effective Date: {EFFECTIVE_DATE}
              <span className="mx-2 text-gray-300">·</span>
              Last Updated: {LAST_UPDATED}
            </p>
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3 mt-4">
              This is placeholder legal copy pending final review by counsel. Do not rely on
              it for compliance purposes until it has been formally approved.
            </p>
            <p className="text-sm text-body mt-4 leading-relaxed">
              This Privacy Policy describes how Fash (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects, uses,
              and shares information when you use our website, apps, and related services.
            </p>
          </div>

          <Section id="collection" number="1" title="Information We Collect">
            <p>
              We collect information you provide directly (such as name, email, phone number,
              delivery address, and payment details processed by Stripe), information about
              your orders and interactions with boutiques, and technical data such as device
              type, browser, and usage logs.
            </p>
          </Section>

          <Section id="use" number="2" title="How We Use Information">
            <p>
              We use your information to operate the marketplace, process orders and payments,
              facilitate delivery, provide customer support, send transactional notifications,
              improve the Service, prevent fraud, and comply with legal obligations.
            </p>
          </Section>

          <Section id="sharing" number="3" title="How We Share Information">
            <p>
              We share information with boutiques fulfilling your orders, delivery partners,
              payment processors (including Stripe), analytics and infrastructure providers,
              and when required by law. We do not sell your personal information.
            </p>
          </Section>

          <Section id="cookies" number="4" title="Cookies & Analytics">
            <p>
              We use cookies and similar technologies to keep you signed in, remember
              preferences, and understand how the Service is used. You can control cookies
              through your browser settings, though some features may not work correctly if
              disabled.
            </p>
          </Section>

          <Section id="retention" number="5" title="Data Retention">
            <p>
              We retain information for as long as needed to provide the Service, resolve
              disputes, enforce agreements, and meet legal requirements. Retention periods
              vary based on the type of data and applicable law.
            </p>
          </Section>

          <Section id="rights" number="6" title="Your Choices & Rights">
            <p>
              You may update account information in Settings, manage notification
              preferences, and request access, correction, or deletion of your data where
              applicable by contacting us. California and other state privacy rights may
              apply depending on your location.
            </p>
          </Section>

          <Section id="security" number="7" title="Security">
            <p>
              We implement administrative, technical, and organizational measures designed to
              protect your information. No method of transmission or storage is completely
              secure, and we cannot guarantee absolute security.
            </p>
          </Section>

          <Section id="children" number="8" title="Children">
            <p>
              The Service is not directed to children under 13, and we do not knowingly
              collect personal information from them. If you believe a child has provided us
              data, contact us so we can delete it.
            </p>
          </Section>

          <Section id="changes" number="9" title="Changes">
            <p>
              We may update this Privacy Policy from time to time. Material changes will be
              posted on this page with an updated &quot;Last Updated&quot; date.
            </p>
          </Section>

          <Section id="contact" number="10" title="Contact">
            <p>
              Privacy questions? Email{' '}
              <a href="mailto:support@fashapp.com" className="text-primary hover:underline">
                support@fashapp.com
              </a>
              . See also our{' '}
              <Link to={ROUTES.terms} className="text-primary hover:underline">
                Terms of Service
              </Link>
              .
            </p>
          </Section>
        </div>
      </main>
    </div>
  );
}
